import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generateAIResponseForMentorship,
  useGetMentorshipAIChatHistory,
} from "@/lib/api/ai";
import { toAiChatMessages, type AiChatMessage } from "./ai-chat-model";

export interface UseAiChatResult {
  messages: AiChatMessage[];
  pendingQuestion: string | null;
  streamingAnswer: string;
  isLoadingHistory: boolean;
  isStreaming: boolean;
  error: boolean;
  isEmpty: boolean;
  send: (message: string) => void;
  retry: () => void;
}

export function useAiChat(
  mentorshipId: string | null,
  active = true,
): UseAiChatResult {
  const {
    data: history,
    isLoading: isLoadingHistory,
    refetch,
  } = useGetMentorshipAIChatHistory(mentorshipId ?? "", active);

  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [streamingAnswer, setStreamingAnswer] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(false);

  const inFlight = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const messages = useMemo(() => toAiChatMessages(history), [history]);

  const runSend = useCallback(
    async (message: string) => {
      if (!mentorshipId || inFlight.current) return;
      const trimmed = message.trim();
      if (trimmed.length === 0) return;

      const controller = new AbortController();
      abortRef.current = controller;
      inFlight.current = true;
      setPendingQuestion(trimmed);
      setStreamingAnswer("");
      setIsStreaming(true);
      setError(false);

      try {
        const response = await generateAIResponseForMentorship(
          mentorshipId,
          trimmed,
          controller.signal,
        );
        if (!response.ok || !response.body) {
          throw new Error(`AI chat request failed (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let done = false;
        while (!done) {
          const chunk = await reader.read();
          done = chunk.done;
          if (chunk.value) {
            setStreamingAnswer(
              (prev) => prev + decoder.decode(chunk.value, { stream: true }),
            );
          }
        }
        const tail = decoder.decode();
        if (tail) setStreamingAnswer((prev) => prev + tail);
      } catch {
        if (controller.signal.aborted) return;
        setError(true);
        setStreamingAnswer("");
        setIsStreaming(false);
        inFlight.current = false;
        return;
      }
      setIsStreaming(false);

      try {
        await refetch();
        setPendingQuestion(null);
        setStreamingAnswer("");
      } catch {
        // History refresh failed, leave streamed visible.
      } finally {
        inFlight.current = false;
      }
    },
    [mentorshipId, refetch],
  );

  const send = useCallback(
    (message: string) => {
      void runSend(message);
    },
    [runSend],
  );

  const retry = useCallback(() => {
    if (pendingQuestion) void runSend(pendingQuestion);
  }, [pendingQuestion, runSend]);

  const isEmpty =
    messages.length === 0 && pendingQuestion === null && !isStreaming;

  return {
    messages,
    pendingQuestion,
    streamingAnswer,
    isLoadingHistory,
    isStreaming,
    error,
    isEmpty,
    send,
    retry,
  };
}
